import { OctTable } from "@oceantech/oceantech-ui";
import {useContext, useEffect, useMemo, useState} from "react";
import { Button, InputGroup } from "react-bootstrap";
import DatePickerCustom from "../component/date-picker/DatePickerCustom";
import LabelRequired from "../component/LabelRequired";
import TextValidator from "../component/TextValidator";
import { IConfigTable } from "../models/OctResponse";
import {
  CODE_SUCCESS,
  ERROR_MESSAGE,
  INIT_SEARCH_OBJECT,
  TYPE,
} from "../utils/Constant";
import {
  EVALUATE_STATUS,
  MESSAGE_CONFIRM_DIALOG,
  TYPE_CONFIRM_DIALOG,
} from "./constants/CorrectiveSubsectionModal";
import "./CorrectiveSubsection.scss";
import CorrectiveSubsectionModal from "./modals/CorrectiveSubsectionModal";
import { AppContext } from "../appContext/AppContext";
import {
  getListCorrectiveSubsection,
  updateStatusAssessment,
} from "./services/CorrectiveSubsectionServices";
import { toast } from "react-toastify";
import { ConfirmDialog } from "../component/ConfirmDialog";
import { ISearchCorrectiveSubsection } from "./models/CorrectiveSubsectionModels";
import CorrectionColumns from "./columns";
import {CORRECTIVE_SUBSECTION_MODEL_JSON} from "./constants/commons";
import {CSProvider} from "./contexts";

const CorrectiveSubsection = () => {
  const { setIsLoading } = useContext(AppContext);
  const [objectSearch, setObjectSearch] = useState<ISearchCorrectiveSubsection>({
    ...INIT_SEARCH_OBJECT,
    pageSize: 20,
  });
  const [data, setData] = useState<any[]>([]);
  const [reload, setReload] = useState(false);
  const [itemDetail, setItemDetail] = useState<any>(null);
  const [isView, setIsView] = useState<boolean>(false);
  const [configTable, setConfigTable] = useState<IConfigTable>({
    totalPages: 1,
    totalElements: 5,
    numberOfElements: 5,
  });
  const [showModalCriteria, setShowModalCriteria] = useState<boolean>(false);
  const [dataChecked, setDataChecked] = useState<any[]>([]);
  const [showModalConfirm, setShowModalConfirm] = useState({
    open: false,
    message: "",
    type: "",
  });

  useEffect(() => {
    handleGetListCriteria();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, objectSearch.pageIndex, objectSearch.pageSize]);

  const handleGetListCriteria = async () => {
    const params = {
      ...objectSearch,
      planId: objectSearch?.plan?.id,
      planName: objectSearch?.plan?.name,
    };
    delete params.plan;
    setIsLoading(true);
    try {
      const { data } = await getListCorrectiveSubsection(params);
      if (data?.code === CODE_SUCCESS) {
        setData(data?.data?.content);
        setConfigTable({
          totalPages: data?.data?.totalPages,
          totalElements: data?.data?.totalElements,
          numberOfElements: data?.data?.numberOfElements,
        });
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCorrectionModal = (itemDetail: any) => {
    setShowModalCriteria(true);
    setItemDetail(itemDetail);
  };

  const correctionColumns = useMemo(() => CorrectionColumns({
    handleOpenCorrectionModal,
  }), []);

  const handleCloseCriteriaModal = () => {
    setShowModalCriteria(false);
    setReload(!reload);
    setItemDetail(null);
  }

  const handleUpdateStatus = async () => {
    setIsLoading(true);
    const dataRequest = {
      status: EVALUATE_STATUS.EVALUATED.code,
      ids: dataChecked?.map((item: any) => item?.id),
    };
    try {
      const { data } = await updateStatusAssessment(dataRequest);

      if (data?.code === CODE_SUCCESS) {
        toast.success("Hoàn thành đánh giá thành công!");
        handleCloseConfirmDialog();
        setReload(!reload);
      }
    } catch (error) {
      console.error("error", error);
      toast.error(ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenConfirmDialog = (message: string, type: string) => {
    const isEvaluation = type === TYPE_CONFIRM_DIALOG.COMPLETE_EVALUATE
    if (!dataChecked?.length && isEvaluation) {
      return toast.warning("Vui lòng chọn tiêu chí!");
    }

    const hasEvaluatedCriteria = dataChecked.some(
      (item: any) => item?.status === EVALUATE_STATUS.EVALUATED.code
    );

    if (hasEvaluatedCriteria && isEvaluation) {
      return toast.warning("Có tiêu chí đã được hoàn thành đánh giá!");
    }

    setShowModalConfirm({
      open: true,
      message: message,
      type: type,
    });
  };

  const handleCloseConfirmDialog = () => {
    setShowModalConfirm({ open: false, message: "", type: "" });
    setDataChecked([]);
  };

  const handleConfirm = () => {
    const options: { [key: string]: () => Promise<void> } = {
      [TYPE_CONFIRM_DIALOG.COMPLETE_EVALUATE]: () => handleUpdateStatus(),
    };
    options[showModalConfirm?.type]?.();
  };

  const handleChange = (name: string, value: any) => {
    setObjectSearch({
      ...objectSearch,
      [name]: value,
    });
  };

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      handleSubmitSearch();
    }
  };

  const handleSubmitSearch = () => {
    setReload(!reload);
    setObjectSearch({
      ...objectSearch,
      pageIndex: 1,
    });
  };

  const contextValue = useMemo(() => ({
    itemDetail,
    setItemDetail,
    isView,
    setIsView,
  }), [itemDetail, isView])

  return (
    <div className="pe-list">
      <CSProvider value={contextValue}>
        <div className="h-100 bg-white">
          <div className="reception-list__container spaces py-0">
            <div className="px-3 pt-2 border-bottom">
              <div className="text-title">Danh sách khắc phục</div>
            </div>

            <div className="d-flex justify-content-between align-items-center px-3 pt-2">
              <Button
                className="btn-fill spaces min-w-150 h-29"
                onClick={() =>
                  handleOpenConfirmDialog(
                    MESSAGE_CONFIRM_DIALOG[TYPE_CONFIRM_DIALOG.COMPLETE_EVALUATE],
                    TYPE_CONFIRM_DIALOG.COMPLETE_EVALUATE
                  )
                }
              >
                Tổng hợp đánh giá
              </Button>
              <div className="d-flex spaces mx-10 align-items-end">
                <LabelRequired className="max-content-width spaces mr-5" label="Từ ngày"/>
                <DatePickerCustom
                  placeholderText="dd/mm/yyyy"
                  name="startDate"
                  value={objectSearch?.startDate ?? ""}
                  setDateValue={(date) => {
                    handleChange("startDate", date);
                  }}
                  dateFormatOutput="YYYY-MM-DD"
                />
              </div>
              <div className="d-flex spaces mx-10 align-items-end">
                <LabelRequired className="max-content-width spaces mr-5" label="Đến ngày"/>
                <DatePickerCustom
                  placeholderText="dd/mm/yyyy"
                  name="endDate"
                  value={objectSearch?.endDate ?? ""}
                  setDateValue={(date) => {
                    handleChange("endDate", date);
                  }}
                  dateFormatOutput="YYYY-MM-DD"
                />
              </div>
              <div className="text-lable-input mb-0 spaces spaces mt-5 mr-5 form-label max-content-width">Từ khóa</div>
              <div className="flex-grow-1">
                <InputGroup className="spaces h-29 px-0">
                  <TextValidator
                    className="spaces h-29 min-w-200 rounded-end-0"
                    name="keyword"
                    placeholder="Tìm kiếm theo mã, tên kế hoạch"
                    value={objectSearch?.keyword || ""}
                    onChange={(e: any) =>
                      handleChange("keyword", e.target.value)
                    }
                    onKeyDown={handleKeyPress}
                  />
                  <Button
                    className="btn-fill spaces h-29"
                    onClick={handleSubmitSearch}
                  >
                    <i className="bi bi-search"></i>
                    <span>Lọc</span>
                  </Button>
                </InputGroup>
              </div>
            </div>

            <OctTable
              id="searchCriteria"
              className={`mt-7 table-background-th width-100 spaces h-calc-vh-230`}
              data={data}
              columns={correctionColumns}
              searchObject={objectSearch}
              setSearchObject={setObjectSearch}
              fixedColumnsCount={-1}
              noToolbar={true}
              totalPages={configTable.totalPages}
              totalElements={configTable.totalElements}
              numberOfElements={configTable.numberOfElements}
              // handleDoubleClick={handleDoubleClick}
              isShowSelection={false}
              clearToolbar={true}
              type={TYPE.MULTILINE}
              setDataChecked={(dataChecked) => setDataChecked(dataChecked)}
            />
          </div>
        </div>

        {showModalCriteria && (
          <CorrectiveSubsectionModal
            show={showModalCriteria}
            handleCloseDialog={handleCloseCriteriaModal}
            type={CORRECTIVE_SUBSECTION_MODEL_JSON.CORRECTION_FORM}
            itemDetail={itemDetail}
            setItemDetail={setItemDetail}
          />
        )}

        {showModalConfirm && (
          <ConfirmDialog
            show={showModalConfirm.open}
            message={showModalConfirm.message}
            yes="Xác nhận"
            onYesClick={handleConfirm}
            cancel="Hủy"
            onCancelClick={handleCloseConfirmDialog}
          />
        )}
      </CSProvider>
    </div>
  );
};

export default CorrectiveSubsection;
